import { useState } from "react";
import { toast } from "sonner";
import { CloudDownload, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, fieldInputCls } from "@/components/Field";
import { WeatherIcon } from "@/components/WeatherIcon";
import { useT } from "@/lib/i18n";
import { WEATHER } from "@/lib/format";
import { api, formatApiError } from "@/lib/api";

const DIRS = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
export const dirOf = (deg) => (deg == null || deg === "" ? "" : DIRS[Math.round((Number(deg) % 360) / 45) % 8]);

const todayIso = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; };
export const fetchEventWeather = async (eventId, lang = "it") => (await api.get(`/events/${eventId}/weather`, { params: { today: todayIso(), lang } })).data;

const LiveWeatherBtn = ({ eventId, values, onChange, info, setInfo }) => {
  const { t, lang } = useT();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const w = await fetchEventWeather(eventId, lang);
      if (!w.conditions) { toast.error(t("no_track_coords")); return; }
      onChange({ ...values, ...w.conditions });
      setInfo(w);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const time = info?.dt ? new Date(info.dt * 1000).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" data-testid="use-live-weather-btn" onClick={run} disabled={busy}
        className="flex items-center gap-1.5 h-9 px-3 rounded-sm border border-[#007AFF]/60 bg-[#007AFF]/10 text-[#007AFF] font-mono text-xs uppercase hover:bg-[#007AFF]/20 transition-colors duration-200 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudDownload className="w-4 h-4" />} {t("use_live_weather")}
      </button>
      {info?.conditions && <span data-testid="weather-source-note" className="font-mono text-[11px] text-slate-400">{t("weather_from").replace("{track}", info.track).replace("{time}", time)}{info.description ? ` · ${info.description}` : ""}</span>}
    </div>
  );
};

export const ConditionsFields = ({ values, onChange, eventId, info, setInfo }) => {
  const { t } = useT();
  const setNum = (k) => (e) => onChange({ ...values, [k]: e.target.value === "" ? null : e.target.value });
  const num = (k, label, step = "0.5") => (
    <Field label={t(label)}>
      <Input data-testid={`${k.replace("_", "-")}-input`} type="number" step={step} inputMode="decimal" value={values[k] ?? ""} onChange={setNum(k)} className={fieldInputCls} />
    </Field>
  );
  return (
    <div data-testid="conditions-section" className="bg-[#090A0F] border border-[#282D3D] rounded-sm p-4 space-y-4">
      <div className="font-heading uppercase font-bold text-lg">{t("conditions")}</div>
      {eventId && <LiveWeatherBtn eventId={eventId} values={values} onChange={onChange} info={info} setInfo={setInfo} />}
      <div className="flex flex-wrap gap-2">
        {WEATHER.map((w) => {
          const active = values.weather === w;
          return (
            <button type="button" key={w} data-testid={`weather-${w}-btn`} onClick={() => onChange({ ...values, weather: active ? "" : w })}
              className={`flex items-center gap-1.5 px-3 py-2 border rounded-sm text-xs font-mono uppercase transition-colors duration-200 ${active ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-[#282D3D] text-slate-400 hover:text-white hover:border-slate-500"}`}>
              <WeatherIcon weather={w} /> {t(`weather_${w}`)}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {num("air_temp", "air_temp")}
        {num("track_temp", "track_temp")}
        {num("humidity", "humidity_pct", "1")}
        {num("wind_speed", "wind_kmh", "1")}
        <Field label={t("wind_dir")}>
          <Select value={dirOf(values.wind_deg) || "none"} onValueChange={(d) => onChange({ ...values, wind_deg: d === "none" ? null : DIRS.indexOf(d) * 45 })}>
            <SelectTrigger data-testid="wind-dir-select" className={fieldInputCls}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {DIRS.map((d) => <SelectItem key={d} value={d} data-testid={`wind-dir-${d}`}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        {num("pressure", "pressure_hpa", "1")}
      </div>
    </div>
  );
};
